import { useMemo } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  MiniMap,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { ValueNode } from './nodes/ValueNode';
import { CompetencyNode } from './nodes/CompetencyNode';
import { ActionNode } from './nodes/ActionNode';
import { EntryNode } from './nodes/EntryNode';
import './FlowchartView.css';

// Node types shared across flowchart views
const baseNodeTypes = {
  valueNode: ValueNode,
  competencyNode: CompetencyNode,
  actionNode: ActionNode,
  entryNode: EntryNode,
};

// Default minimap color function
const defaultNodeColor = (node) => {
  switch (node.type) {
    case 'valueNode':
      return '#2A987A';
    case 'competencyNode':
      return '#d4e4fc';
    case 'actionNode':
      return node.data?.isMultiParent ? '#fef3c7' : '#ffffff';
    case 'entryNode':
      return '#fef3c7';
    default:
      return '#e5e7eb';
  }
};

/**
 * Shared base flowchart component
 * @param {Object} props
 * @param {Array} props.nodes - React Flow nodes
 * @param {Array} props.edges - React Flow edges
 * @param {string} props.direction - 'LR' or 'TB'
 * @param {Function} props.onNodeClick - Optional node click handler
 * @param {Array} props.legendItems - Legend items to display
 * @param {string} props.className - Additional CSS class
 * @param {Object} props.fitViewOptions - Custom fitView options
 */
export function BaseFlowchart({
  nodes,
  edges,
  direction = 'LR',
  onNodeClick,
  legendItems = [],
  className = '',
  fitViewOptions = {},
}) {
  // Update node positions based on direction
  const nodesWithPositions = useMemo(() => {
    const sourcePos = direction === 'LR' ? Position.Right : Position.Bottom;
    const targetPos = direction === 'LR' ? Position.Left : Position.Top;

    return nodes.map(node => ({
      ...node,
      sourcePosition: sourcePos,
      targetPosition: targetPos,
    }));
  }, [nodes, direction]);

  const defaultFitViewOptions = {
    padding: 0.2,
    minZoom: 0.05,
    maxZoom: 1.5,
    ...fitViewOptions,
  };

  return (
    <div className={`flowchart-container ${className}`} data-direction={direction}>
      <ReactFlow
        nodes={nodesWithPositions}
        edges={edges}
        nodeTypes={baseNodeTypes}
        onNodeClick={onNodeClick}
        fitView
        fitViewOptions={defaultFitViewOptions}
        minZoom={0.05}
        maxZoom={2}
        attributionPosition="bottom-right"
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#e5e7eb" gap={20} />
        <Controls showInteractive={false} />
        <MiniMap
          nodeColor={defaultNodeColor}
          maskColor="rgba(0, 0, 0, 0.1)"
          style={{ backgroundColor: '#f8f9fa' }}
        />
      </ReactFlow>

      {legendItems.length > 0 && (
        <div className="flowchart-legend">
          {legendItems.map((item, idx) => (
            <div key={idx} className="legend-item">
              <span className={`legend-dot legend-dot-${item.type}`}></span>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
